import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { createQrDesign } from "../../redux/actions/qrDesignActions";
import { toast } from "react-toastify";
import { FaArrowLeft, FaImage, FaUpload } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { FullScreenLoader } from "../Components/Loader";

const CreateQRDesign = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { creating } = useSelector((state) => state.qrDesign || {});

  const [formData, setFormData] = useState({
    qr_image: null,
    imagePreview: null,
    bottom_percent: "10",
    right_percent: "50",
    rotation_percent: "0",
    qr_size_percent: "15",
    is_active: true,
    qr_rows: "",
    qr_cols: "",
  });

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [previewScale, setPreviewScale] = useState(1);
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);
  const qrOverlayRef = useRef(null);
  const imageRef = useRef(null);
const { access_modules } = useSelector((state) => state.dealerAccess);
const qrPerms = access_modules?.data?.find((m) => m.alias === "qr_design");
const canCreate = !!qrPerms?.is_create
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSliderChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value.toString(),
    }));
  };

  const calculateMargins = (leftX, topY, containerWidth, containerHeight, qrWidth, qrHeight) => {
    const qrCenterX = leftX + qrWidth / 2;
    const qrCenterY = topY + qrHeight / 2;
    const rightDistance = containerWidth - qrCenterX;
    const rightPercent = Math.max(0, Math.min(100, (rightDistance / containerWidth) * 100));
    const bottomDistance = containerHeight - qrCenterY;
    const bottomPercent = Math.max(0, Math.min(100, (bottomDistance / containerHeight) * 100));
    return {
      right_percent: rightPercent.toFixed(2),
      bottom_percent: bottomPercent.toFixed(2),
    };
  };

  const handleDragStart = (e) => {
    if (!formData.imagePreview || !qrOverlayRef.current || !imageRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const qrElement = qrOverlayRef.current;
    const imageElement = imageRef.current;
    const qrRect = qrElement.getBoundingClientRect();
    const imageRect = imageElement.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    const offsetX = clientX - qrRect.left - qrRect.width / 2;
    const offsetY = clientY - qrRect.top - qrRect.height / 2;
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleDrag = useCallback((e) => {
    if (!isDragging || !formData.imagePreview || !qrOverlayRef.current || !imageRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const qrElement = qrOverlayRef.current;
    const imageElement = imageRef.current;
    const imageRect = imageElement.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    const renderedImageWidth = imageRect.width;
    const renderedImageHeight = imageRect.height;
    const mouseX = clientX - imageRect.left;
    const mouseY = clientY - imageRect.top;
    const qrSize = qrElement.offsetWidth || qrElement.clientWidth;
    let qrCenterX = mouseX - dragOffset.x;
    let qrCenterY = mouseY - dragOffset.y;
    qrCenterX = Math.max(qrSize / 2, Math.min(renderedImageWidth - qrSize / 2, qrCenterX));
    qrCenterY = Math.max(qrSize / 2, Math.min(renderedImageHeight - qrSize / 2, qrCenterY));
    const leftX = qrCenterX - qrSize / 2;
    const topY = qrCenterY - qrSize / 2;
    if (imageDimensions.width > 0 && imageDimensions.height > 0) {
      const scaleX = imageDimensions.width / renderedImageWidth;
      const scaleY = imageDimensions.height / renderedImageHeight;
      const naturalLeftX = leftX * scaleX;
      const naturalTopY = topY * scaleY;
      const naturalQrSize = qrSize * Math.min(scaleX, scaleY);
      const margins = calculateMargins(
        naturalLeftX,
        naturalTopY,
        imageDimensions.width,
        imageDimensions.height,
        naturalQrSize,
        naturalQrSize
      );
      setFormData((prev) => ({
        ...prev,
        right_percent: margins.right_percent,
        bottom_percent: margins.bottom_percent,
      }));
    }
  }, [isDragging, formData.imagePreview, dragOffset, imageDimensions]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDrag, { passive: false });
      document.addEventListener("mouseup", handleDragEnd, { passive: false });
      document.addEventListener("touchmove", handleDrag, { passive: false });
      document.addEventListener("touchend", handleDragEnd, { passive: false });
      return () => {
        document.removeEventListener("mousemove", handleDrag);
        document.removeEventListener("mouseup", handleDragEnd);
        document.removeEventListener("touchmove", handleDrag);
        document.removeEventListener("touchend", handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, qr_image: "Please select a valid image file" }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, qr_image: "Image size should be less than 5MB" }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = reader.result;
      setFormData((prev) => ({
        ...prev,
        qr_image: reader.result,
        imagePreview: reader.result,
      }));
      setErrors((prev) => ({ ...prev, qr_image: "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      requestAnimationFrame(() => {
        const renderedWidth = img.offsetWidth || img.clientWidth;
        const renderedHeight = img.offsetHeight || img.clientHeight;
        if (renderedWidth && renderedHeight && img.naturalWidth && img.naturalHeight) {
          const scaleX = renderedWidth / img.naturalWidth;
          const scaleY = renderedHeight / img.naturalHeight;
          setPreviewScale(Math.min(scaleX, scaleY));
          if (formData.right_percent && formData.bottom_percent && formData.qr_size_percent) {
            const diagonal = Math.sqrt(img.naturalWidth ** 2 + img.naturalHeight ** 2);
            const qrSizeNatural = (diagonal * parseFloat(formData.qr_size_percent)) / 100;
            const rightPercent = parseFloat(formData.right_percent);
            const bottomPercent = parseFloat(formData.bottom_percent);
            const centerX = img.naturalWidth * (1 - rightPercent / 100);
            const centerY = img.naturalHeight * (1 - bottomPercent / 100);
            const leftX = centerX - qrSizeNatural / 2;
            const topY = centerY - qrSizeNatural / 2;
            const margins = calculateMargins(
              leftX, topY,
              img.naturalWidth, img.naturalHeight,
              qrSizeNatural, qrSizeNatural
            );
            setFormData((prev) => ({
              ...prev,
              right_percent: margins.right_percent,
              bottom_percent: margins.bottom_percent,
            }));
          }
        }
      });
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, qr_image: null, imagePreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.qr_image) newErrors.qr_image = "QR design image is required";
    if (!formData.qr_rows || parseInt(formData.qr_rows) < 1) newErrors.qr_rows = "Enter valid row count";
    if (!formData.qr_cols || parseInt(formData.qr_cols) < 1) newErrors.qr_cols = "Enter valid column count";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if (!validateForm()) {
    //   toast.error("Please fix the errors in the form");
    //   return;
    // }
    try {
      const qrDesignData = {
        qr_image: formData.qr_image,
        bottom_percent: formData.bottom_percent,
        right_percent: formData.right_percent,
        rotation_percent: formData.rotation_percent,
        qr_size_percent: formData.qr_size_percent,
        is_active: formData.is_active,
        qr_count: `${formData.qr_rows}*${formData.qr_cols}`,
      };
      await dispatch(createQrDesign(qrDesignData, userInfo.access_token));
      toast.success("QR Design created successfully!");
      navigate("/dashboard/qrdesignlist");
    } catch (error) {
      console.error("Error creating QR design:", error);
    }
  };
// ✅ ADD HERE
  if (!canCreate && access_modules?.data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-red-500 font-bold text-lg">Access Denied</p>
          <p className="text-gray-500 text-sm mt-2">You don't have permission to create QR Designs.</p>
        </div>
      </DashboardLayout>
    );
  }

  const rows = parseInt(formData.qr_rows) || 0;
  const cols = parseInt(formData.qr_cols) || 0;
  const totalQR = rows * cols;
  const previewRows = Math.min(rows, 8);
  const previewCols = Math.min(cols, 12);

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={creating}
        message={creating ? "Creating QR design..." : ""}
        variant="primary"
        blurIntensity="md"
      />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center space-x-3 mb-6">
            <button onClick={() => navigate(-1)} className="hover:text-gray-800 transition">
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600 mb-1">Create QR Design</h2>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Left Panel - Configuration */}
              <div className="space-y-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-amber-500">
                  Configuration
                </h4>

                {/* Upload Base Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Base Image <span className="text-red-500">*</span>
                  </label>
                  {formData.imagePreview ? (
                    <div className="relative">
                      <img
                        src={formData.imagePreview}
                        alt="Preview"
                        className="w-full h-64 object-contain rounded-lg border-2 border-gray-300 bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                      >
                        <FiX size={20} />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 transition"
                    >
                      <FaUpload className="mx-auto text-gray-400 text-4xl mb-2" />
                      <p className="text-gray-600 mb-1">Click to upload image</p>
                      <p className="text-gray-500 text-sm">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  {errors.qr_image && <p className="text-red-500 text-sm mt-1">{errors.qr_image}</p>}
                </div>

                {/* Position Display */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bottom Position</label>
                    <p className="text-sm font-semibold text-gray-800">{parseFloat(formData.bottom_percent).toFixed(1)}%</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Right Position</label>
                    <p className="text-sm font-semibold text-gray-800">{parseFloat(formData.right_percent).toFixed(1)}%</p>
                  </div>
                  <p className="text-xs text-gray-500 col-span-2 mt-2">
                    Drag the QR code in the preview to adjust position
                  </p>
                </div>

                {/* Rotation Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Rotation: {formData.rotation_percent}°
                    </label>
                  </div>
                  <input
                    type="range"
                    name="rotation_percent"
                    min="0"
                    max="360"
                    value={formData.rotation_percent}
                    onChange={(e) => handleSliderChange("rotation_percent", e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">QR code rotation angle</p>
                </div>

                {/* QR Size Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      QR Size: {formData.qr_size_percent}%
                    </label>
                  </div>
                  <input
                    type="range"
                    name="qr_size_percent"
                    min="1"
                    max="50"
                    value={formData.qr_size_percent}
                    onChange={(e) => handleSliderChange("qr_size_percent", e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">QR code size as percentage of image diagonal</p>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Make QR design active immediately
                  </label>
                </div>

            
              </div>

              {/* Right Panel - Live Preview */}
              <div className="space-y-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-amber-500">
                  Live Preview
                </h4>
                <div
                  ref={previewContainerRef}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 min-h-[400px] flex items-center justify-center bg-gray-50 relative"
                >
                  {formData.imagePreview ? (
                    <div className="relative w-full h-full">
                      <img
                        ref={imageRef}
                        src={formData.imagePreview}
                        alt="Preview"
                        className="w-full h-auto max-h-[400px] object-contain rounded-lg"
                        onLoad={handleImageLoad}
                      />
                      {formData.qr_image && imageRef.current && imageDimensions.width > 0 && imageDimensions.height > 0 && (() => {
                        const img = imageRef.current;
                        const renderedWidth = img.offsetWidth || img.clientWidth || 0;
                        const renderedHeight = img.offsetHeight || img.clientHeight || 0;
                        if (renderedWidth === 0 || renderedHeight === 0) return null;
                        const rightPercent = parseFloat(formData.right_percent || 0);
                        const bottomPercent = parseFloat(formData.bottom_percent || 0);
                        const naturalDiagonal = Math.sqrt(imageDimensions.width ** 2 + imageDimensions.height ** 2);
                        const naturalQrSize = (naturalDiagonal * parseFloat(formData.qr_size_percent || 15)) / 100;
                        const naturalX = imageDimensions.width * (1 - rightPercent / 100) - (naturalQrSize / 2);
                        const naturalY = imageDimensions.height * (1 - bottomPercent / 100) - (naturalQrSize / 2);
                        const scaleX = renderedWidth / imageDimensions.width;
                        const scaleY = renderedHeight / imageDimensions.height;
                        const previewX = naturalX * scaleX;
                        const previewY = naturalY * scaleY;
                        const previewQrSize = naturalQrSize * Math.min(scaleX, scaleY);
                        const displaySize = Math.max(50, Math.min(200, previewQrSize));

                        return (
                          <div
                            ref={qrOverlayRef}
                            onMouseDown={handleDragStart}
                            onTouchStart={handleDragStart}
                            draggable={false}
                            className={`absolute bg-white cursor-move select-none ${
                              isDragging ? "opacity-90 shadow-2xl z-10" : "opacity-90 hover:opacity-95"
                            } transition-opacity border-2 border-gray-800`}
                            style={{
                              left: `${previewX}px`,
                              top: `${previewY}px`,
                              transform: `rotate(${formData.rotation_percent}deg)`,
                              width: `${displaySize}px`,
                              height: `${displaySize}px`,
                              minWidth: "50px",
                              minHeight: "50px",
                              touchAction: "none",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              MozUserSelect: "none",
                              msUserSelect: "none",
                              aspectRatio: "1 / 1",
                            }}
                            onDragStart={(e) => e.preventDefault()}
                            title="Drag to move QR code position"
                          >
                            <div className="w-full h-full bg-white p-1">
                              <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                                <rect x="5" y="5" width="25" height="25" fill="black" rx="2" />
                                <rect x="10" y="10" width="15" height="15" fill="white" />
                                <rect x="12" y="12" width="11" height="11" fill="black" />
                                <rect x="70" y="5" width="25" height="25" fill="black" rx="2" />
                                <rect x="75" y="10" width="15" height="15" fill="white" />
                                <rect x="77" y="12" width="11" height="11" fill="black" />
                                <rect x="5" y="70" width="25" height="25" fill="black" rx="2" />
                                <rect x="10" y="75" width="15" height="15" fill="white" />
                                <rect x="12" y="77" width="11" height="11" fill="black" />
                                <rect x="42" y="42" width="16" height="16" fill="black" rx="1" />
                                <rect x="45" y="45" width="10" height="10" fill="white" />
                                <rect x="47" y="47" width="6" height="6" fill="black" />
                                <rect x="35" y="5" width="3" height="3" fill="black" />
                                <rect x="40" y="5" width="3" height="3" fill="black" />
                                <rect x="50" y="5" width="3" height="3" fill="black" />
                                <rect x="55" y="5" width="3" height="3" fill="black" />
                                <rect x="60" y="5" width="3" height="3" fill="black" />
                                <rect x="5" y="35" width="3" height="3" fill="black" />
                                <rect x="10" y="35" width="3" height="3" fill="black" />
                                <rect x="15" y="35" width="3" height="3" fill="black" />
                                <rect x="20" y="35" width="3" height="3" fill="black" />
                                <rect x="25" y="35" width="3" height="3" fill="black" />
                                <rect x="35" y="20" width="3" height="3" fill="black" />
                                <rect x="40" y="20" width="3" height="3" fill="black" />
                                <rect x="50" y="20" width="3" height="3" fill="black" />
                                <rect x="55" y="20" width="3" height="3" fill="black" />
                                <rect x="60" y="20" width="3" height="3" fill="black" />
                                <rect x="35" y="30" width="3" height="3" fill="black" />
                                <rect x="40" y="30" width="3" height="3" fill="black" />
                                <rect x="50" y="30" width="3" height="3" fill="black" />
                                <rect x="55" y="30" width="3" height="3" fill="black" />
                                <rect x="60" y="30" width="3" height="3" fill="black" />
                                <rect x="70" y="35" width="3" height="3" fill="black" />
                                <rect x="75" y="35" width="3" height="3" fill="black" />
                                <rect x="80" y="35" width="3" height="3" fill="black" />
                                <rect x="85" y="35" width="3" height="3" fill="black" />
                                <rect x="90" y="35" width="3" height="3" fill="black" />
                                <rect x="5" y="50" width="3" height="3" fill="black" />
                                <rect x="10" y="50" width="3" height="3" fill="black" />
                                <rect x="15" y="50" width="3" height="3" fill="black" />
                                <rect x="20" y="50" width="3" height="3" fill="black" />
                                <rect x="25" y="50" width="3" height="3" fill="black" />
                                <rect x="35" y="60" width="3" height="3" fill="black" />
                                <rect x="40" y="60" width="3" height="3" fill="black" />
                                <rect x="50" y="60" width="3" height="3" fill="black" />
                                <rect x="55" y="60" width="3" height="3" fill="black" />
                                <rect x="60" y="60" width="3" height="3" fill="black" />
                                <rect x="70" y="50" width="3" height="3" fill="black" />
                                <rect x="75" y="50" width="3" height="3" fill="black" />
                                <rect x="80" y="50" width="3" height="3" fill="black" />
                                <rect x="85" y="50" width="3" height="3" fill="black" />
                                <rect x="90" y="50" width="3" height="3" fill="black" />
                                <rect x="35" y="70" width="3" height="3" fill="black" />
                                <rect x="40" y="70" width="3" height="3" fill="black" />
                                <rect x="50" y="70" width="3" height="3" fill="black" />
                                <rect x="55" y="70" width="3" height="3" fill="black" />
                                <rect x="60" y="70" width="3" height="3" fill="black" />
                                <rect x="70" y="60" width="3" height="3" fill="black" />
                                <rect x="75" y="60" width="3" height="3" fill="black" />
                                <rect x="80" y="60" width="3" height="3" fill="black" />
                                <rect x="85" y="60" width="3" height="3" fill="black" />
                                <rect x="90" y="60" width="3" height="3" fill="black" />
                                <rect x="5" y="80" width="3" height="3" fill="black" />
                                <rect x="10" y="80" width="3" height="3" fill="black" />
                                <rect x="15" y="80" width="3" height="3" fill="black" />
                                <rect x="20" y="80" width="3" height="3" fill="black" />
                                <rect x="25" y="80" width="3" height="3" fill="black" />
                                <rect x="35" y="80" width="3" height="3" fill="black" />
                                <rect x="40" y="80" width="3" height="3" fill="black" />
                                <rect x="50" y="80" width="3" height="3" fill="black" />
                                <rect x="55" y="80" width="3" height="3" fill="black" />
                                <rect x="60" y="80" width="3" height="3" fill="black" />
                                <rect x="70" y="80" width="3" height="3" fill="black" />
                                <rect x="75" y="80" width="3" height="3" fill="black" />
                                <rect x="80" y="80" width="3" height="3" fill="black" />
                                <rect x="85" y="80" width="3" height="3" fill="black" />
                                <rect x="90" y="80" width="3" height="3" fill="black" />
                                <rect x="35" y="90" width="3" height="3" fill="black" />
                                <rect x="40" y="90" width="3" height="3" fill="black" />
                                <rect x="50" y="90" width="3" height="3" fill="black" />
                                <rect x="55" y="90" width="3" height="3" fill="black" />
                                <rect x="60" y="90" width="3" height="3" fill="black" />
                              </svg>
                            </div>
                            {isDragging && (
                              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                Dragging...
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center">Upload an image to see preview</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 sm:gap-4 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-10 h-10 sm:w-auto bg-white border-2 border-red-500 text-red-600 p-2 sm:py-2.5 sm:px-6 rounded-lg hover:bg-red-50 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <FiX size={20} className="sm:mr-2" />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {creating ? "Creating..." : "Create QR Design"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateQRDesign;